import MatrixTable from '@adsk/bim360-matrix-react-matrix-table';
import Toggle from '@adsk/bim360-matrix-react-toggle';
import PropTypes from 'prop-types';
import React from 'react';
import {ISSUE_ATTRIBUTE_NAMES} from '../../../constants/issue-attribute-names';
import i18n from '../../../utils/i18n';
import IssueAttributesAdminTypeIconAndName from './IssueAttributesAdminTypeIconAndName';

const getTableData = (requiredFieldsMap, {disableLocation}) =>
  [
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.ISSUE_SUB_TYPE_ID,
      title: i18n.t('issueAttributes.issueType.label'),
      dataType: 'list',
      disabled: true
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.STATUS,
      title: i18n.t('issueAttributes.status.label'),
      dataType: 'list',
      disabled: true
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.TITLE,
      title: i18n.t('issueAttributes.title.label'),
      dataType: 'text',
      disabled: true
    },
    // custom, maybe required
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO,
      title: i18n.t('issueAttributes.assignedTo.label'),
      dataType: 'list'
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.DUE_DATE,
      title: i18n.t('issueAttributes.dueDate.label'),
      dataType: 'list'
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.LOCATION_ID,
      title: i18n.t('issueAttributes.location.label'),
      dataType: 'list',
      disabled: disableLocation
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.LOCATION_DETAILS,
      title: i18n.t('issueAttributes.locationDetails.label'),
      dataType: 'paragraph'
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.LINKED_DOCUMENTS,
      title: i18n.t('issueAttributes.document.label'),
      dataType: ''
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.OWNER_ID,
      title: i18n.t('issueAttributes.owner.label'),
      dataType: 'list'
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.ROOT_CAUSE_ID,
      title: i18n.t('issueAttributes.rootCause.label'),
      dataType: 'list'
    },
    {
      fieldName: ISSUE_ATTRIBUTE_NAMES.DESCRIPTION,
      title: i18n.t('issueAttributes.description.label'),
      dataType: 'paragraph'
    }
  ].map(({fieldName, disabled, ...rest}) => ({
    fieldName,
    permittedAttributes: ['isRequired'],
    enabled: !disabled,
    isRequired: requiredFieldsMap[fieldName],
    ...rest
  }));

export const IssueRequiredAttributesAdminTable = ({requiredFieldsMap, updateRequiredFields, disableLocation}) => {
  const tableData = getTableData(requiredFieldsMap, {disableLocation});
  const columnDefinitions = {
    TITLE: {
      dataKey: 'title',
      title: i18n.t('issueAttributesAdmin.table.titleShort'),
      width: 200,
      resizable: true,
      tooltip: ({cellData}) => cellData,
      tooltipProps: {autoTipOnEllipsis: true}
    },
    TYPE: {
      dataKey: 'dataType',
      title: i18n.t('issueAttributesAdmin.table.type'),
      width: 200,
      resizable: true,
      /* eslint-disable-next-line react/display-name,react/prop-types */
      cellRenderer: ({cellData: dataType, container}) =>
        dataType && (
          <IssueAttributesAdminTypeIconAndName
            dataType={dataType}
            svgIconDataTestId="IssueAttributesAdminTable__type-icon"
            titleDataTestId="IssueAttributesAdminTable__type-name"
            container={container}
          />
        )
    },
    VALUES: {
      title: i18n.t('issueAttributesAdmin.table.values'),
      width: 165,
      resizable: true,
      /* eslint-disable-next-line react/display-name,react/prop-types */
      cellRenderer: () => <span>-</span>
    },
    IS_REQUIRED: {
      title: i18n.t('issueAttributesAdmin.table.isRequired'),
      width: 165,
      resizable: true,
      /* eslint-disable-next-line react/display-name,react/prop-types */
      cellRenderer: ({rowData: {isRequired, enabled, fieldName}}) => (
        <Toggle
          defaultChecked={isRequired}
          onChange={newIsRequired => updateRequiredFields(fieldName, newIsRequired)}
          disabled={!enabled}
        />
      )
    }
  };

  return (
    <MatrixTable
      attachTestId
      className="IssueAttributesAdminTable"
      data={tableData}
      maxHeight={Number.MAX_VALUE}
      rowKey="fieldName"
    >
      <MatrixTable.Column key="title" {...columnDefinitions.TITLE} />
      <MatrixTable.Column key="type" {...columnDefinitions.TYPE} />
      <MatrixTable.Column key="values" {...columnDefinitions.VALUES} />
      <MatrixTable.Column key="isRequired" {...columnDefinitions.IS_REQUIRED} />
    </MatrixTable>
  );
};

IssueRequiredAttributesAdminTable.displayName = 'IssueRequiredAttributesAdminTable';

IssueRequiredAttributesAdminTable.defaultProps = {};

IssueRequiredAttributesAdminTable.propTypes = {
  requiredFieldsMap: PropTypes.object.isRequired,
  updateRequiredFields: PropTypes.func.isRequired,
  disableLocation: PropTypes.bool
};

export default IssueRequiredAttributesAdminTable;
